import Mutex from 'p-mutex';

import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';

import type { BasePlayer } from './player/base_player.js';
import { ImagePlayer } from './player/image_player/image_player.js';
import { AudioPlayer } from './player/media_player/audio_player.js';
import type { StreamPlayer as StreamPlayerType } from './player/media_player/stream_player.js';
import { VideoPlayer } from './player/media_player/video_player.js';
import type { PdfPlayer as PdfPlayerType } from './player/pdf_player/pdf_player.js';
import type { PlayerEvents } from './player_events';
import type { PlayerStore } from './state/player_store.js';
import { SubscribersList } from './state/subscribers_list.js';
import type { DrmConfig, DrmFetchContext, PlaylistItem } from './types.js';

const containerMutexMap = new WeakMap<HTMLElement, Mutex>();

const logger = DebugLogger.getInstance('unified_player_core');

type PlayerContainerOptions = {
  store: PlayerStore;
  playerEvents: PlayerEvents;
  container: HTMLElement;
  fetchRequestHeaders?: () => Promise<Record<string, string> | undefined>;
  onFetchDrmConfig?: (
    item: PlaylistItem,
    context: DrmFetchContext,
  ) => Promise<DrmConfig | undefined>;
};

// A playlist item is rebuilt whenever the asset is re-read, which for a growing
// proxy happens as each segment lands
function isSameSource(
  playlistItem: PlaylistItem | undefined,
  other: PlaylistItem | undefined,
): boolean {
  return (
    playlistItem?.id === other?.id &&
    playlistItem?.contentType === other?.contentType &&
    playlistItem?.url === other?.url &&
    playlistItem?.environmentMap === other?.environmentMap
  );
}

export class PlayerContainer {
  readonly #operationMutex: Mutex;

  #store: PlayerStore;

  #playerEvents: PlayerEvents;

  #container: HTMLElement | undefined;

  #player: BasePlayer<HTMLElement> | undefined;

  #comparePlayer:
    | VideoPlayer
    | ImagePlayer
    | PdfPlayerType
    | StreamPlayerType
    | undefined;

  #subscribersList = new SubscribersList();

  #destroyed = false;

  #fetchRequestHeaders:
    | (() => Promise<Record<string, string> | undefined>)
    | undefined;

  #onFetchDrmConfig:
    | ((
        item: PlaylistItem,
        context: DrmFetchContext,
      ) => Promise<DrmConfig | undefined>)
    | undefined;

  constructor({
    store,
    playerEvents,
    container,
    fetchRequestHeaders,
    onFetchDrmConfig,
  }: PlayerContainerOptions) {
    this.#store = store;
    this.#playerEvents = playerEvents;
    this.#container = container;
    this.#fetchRequestHeaders = fetchRequestHeaders;
    this.#onFetchDrmConfig = onFetchDrmConfig;

    let existingMutex = containerMutexMap.get(container);
    if (existingMutex === undefined) {
      existingMutex = new Mutex();
      containerMutexMap.set(container, existingMutex);
    }
    this.#operationMutex = existingMutex;

    this.#subscribersList.subscribe(
      this.#store.playlist.$currentPlaylistItem,
      async (playlistItem, prevPlaylistItem) => {
        if (isSameSource(playlistItem, prevPlaylistItem)) {
          return;
        }

        await this.#operationMutex.withLock(async () => {
          // The mutex is shared per container element, so a callback can still
          // be queued when this instance is torn down; re-creating a player
          // then races the instance that replaced it
          if (this.#destroyed) {
            return;
          }
          await this.#player?.destroy();
          await this.#init(playlistItem);
        });
      },
    );

    this.#subscribersList.subscribe(
      this.#store.compare.$compareItem,
      async (compareItem, prevCompareItem) => {
        if (isSameSource(compareItem, prevCompareItem)) {
          return;
        }

        await this.#operationMutex.withLock(async () => {
          if (this.#destroyed) {
            return;
          }
          await this.#comparePlayer?.destroy();
          await this.#initCompare(compareItem);
        });
      },
    );

    container.style.background = 'var(--unified-player-background, #000000)';
  }

  async #init(playlistItem: PlaylistItem | undefined) {
    if (!playlistItem) {
      return;
    }
    if (!this.#container) {
      throw new Error('Container not found');
    }
    const options = {
      store: this.#store,
      playerEvents: this.#playerEvents,
      container: this.#container,
      fetchRequestHeaders: this.#fetchRequestHeaders,
      onFetchDrmConfig: this.#onFetchDrmConfig,
    };

    switch (playlistItem.contentType) {
      case 'video':
      case 'audio':
      case 'stream': {
        if (playlistItem.contentType === 'stream') {
          const { StreamPlayer } = await import(
            './player/media_player/stream_player.js'
          );
          this.#player = new StreamPlayer(options);
        } else if (playlistItem.contentType === 'video') {
          this.#player = new VideoPlayer(options);
        } else {
          this.#player = new AudioPlayer(options);
        }
        break;
      }
      case 'image': {
        this.#player = new ImagePlayer(options);
        break;
      }
      case 'pdf': {
        const { PdfPlayer } = await import('./player/pdf_player/pdf_player.js');
        this.#player = new PdfPlayer(options);
        break;
      }
      case 'model': {
        const { Model3dPlayer } = await import(
          './player/model3d_player/model3d_player.js'
        );
        this.#player = new Model3dPlayer(options);
        break;
      }
      default: {
        logger.debug(
          'No player available for content type:',
          playlistItem.contentType,
        );
        return;
      }
    }
    if (!this.#player) {
      return;
    }
    logger.debug('Initializing player for:', playlistItem);
    await this.#player.init();
    await this.#player.load(playlistItem).then(() => {
      // once the player has loaded, we can set an initial duration up so that playableRange is calculated correctly for
      // participants joining sync from the gallery. Seeding 0 for a growing item
      // would collapse the playable range the media has already reported
      if (playlistItem.duration !== undefined) {
        this.#store.playableMedia.setDuration(playlistItem.duration);
      }
    });
  }

  async #initCompare(compareItem: PlaylistItem | undefined) {
    const compareActive = this.#store.compare.$compareActive.get();

    if (!compareItem?.url || !compareActive) {
      return;
    }
    if (
      compareItem.contentType !== 'video' &&
      compareItem.contentType !== 'image' &&
      compareItem.contentType !== 'pdf' &&
      compareItem.contentType !== 'stream'
    ) {
      return;
    }
    if (!this.#container) {
      throw new Error('Container not found');
    }

    const options = {
      store: this.#store,
      playerEvents: this.#playerEvents,
      container: this.#container,
      isComparePlayer: true,
      fetchRequestHeaders: this.#fetchRequestHeaders,
      onFetchDrmConfig: this.#onFetchDrmConfig,
    };
    switch (compareItem.contentType) {
      case 'video': {
        this.#comparePlayer = new VideoPlayer(options);
        break;
      }
      case 'image': {
        this.#comparePlayer = new ImagePlayer(options);
        break;
      }
      case 'pdf': {
        const { PdfPlayer } = await import('./player/pdf_player/pdf_player.js');
        this.#comparePlayer = new PdfPlayer(options);
        break;
      }
      case 'stream': {
        const { StreamPlayer } = await import(
          './player/media_player/stream_player.js'
        );
        this.#comparePlayer = new StreamPlayer(options);
        break;
      }
    }

    if (!this.#comparePlayer) {
      return;
    }

    await this.#comparePlayer.init();
    await this.#comparePlayer.load(compareItem);
    this.#store.setLoadState('loaded');
  }

  requestFullscreen() {
    // requestFullscreen opens the video element in fullscreen,
    // which should clear the segments to not trap the users in the range.
    this.#store.segment.clearCurrentSegment();
    if (!this.#player) {
      this.#store.fullscreen.setFullscreen(false);
      return;
    }
    return this.#player.requestFullscreen().catch((error: unknown) => {
      logger.debug('Fullscreen request failed:', error);
      this.#store.fullscreen.setFullscreen(false);
    });
  }

  exitFullscreen() {
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      return;
    }
    if (document.exitFullscreen) {
      return document.exitFullscreen();
    }
    if (document.webkitExitFullscreen) {
      return document.webkitExitFullscreen();
    }
  }

  async destroy() {
    this.#destroyed = true;
    this.#subscribersList.unsubscribeAll();
    await this.#operationMutex.withLock(async () => {
      await this.#player?.destroy();
      await this.#comparePlayer?.destroy();
    });
  }
}
